import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Database, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  TenderDocumentApi,
  TenderDocumentData,
} from '../services/tenderDocumentApi';
import type { TenderMode } from './SuccessfulTenderModeSelector';
import { theme } from '../styles/theme';

// Backwards-compatible export (other modules import this type).
export interface SuccessfulTenderEntry {
  id: string;
  type: 'url' | 'file';
  value: string;
  file?: File;
}

// Component now ignores per-run selection — the analysis pipeline reads
// the whole bank automatically (filtered by company_id with a hybrid
// retriever + reranker). We keep the prop surface intact so existing
// callers don't need to be touched in this PR; mode/selection inputs
// are accepted but treated as no-ops.
interface SuccessfulTenderUploaderProps {
  entries?: SuccessfulTenderEntry[];
  onChange?: (entries: SuccessfulTenderEntry[]) => void;
  disabled?: boolean;
  uploadToCloudinary?: (file: File) => Promise<string>;
  mode?: TenderMode;
  onModeChange?: (mode: TenderMode) => void;
  selectedBankDocuments?: TenderDocumentData[];
  onBankDocumentsChange?: (documents: TenderDocumentData[]) => void;
  showModeSelector?: boolean;
}

const Pill = styled.div<{ $tone: 'primary' | 'muted' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'primary'
        ? theme.colors.primary[200]
        : theme.colors.border};
  background: ${({ $tone }) =>
    $tone === 'primary'
      ? theme.colors.primary[50]
      : theme.colors.backgroundSecondary};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const PillIcon = styled.div<{ $tone: 'primary' | 'muted' }>`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $tone }) =>
    $tone === 'primary'
      ? theme.colors.primary[100]
      : theme.colors.background};
  color: ${({ $tone }) =>
    $tone === 'primary'
      ? theme.colors.primary[700]
      : theme.colors.textMuted};
`;

const PillBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const PillTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
`;

const PillDescription = styled.div`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.xs};
  margin-top: 2px;
`;

const PillAction = styled(Link)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[700]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.primary[200]};
  text-decoration: none;
  transition: background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
  }
`;

const SuccessfulTenderUploader: React.FC<SuccessfulTenderUploaderProps> = () => {
  const [count, setCount] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await TenderDocumentApi.getTenderDocumentStats();
        if (cancelled) return;
        if (resp.success && resp.data) {
          setCount(resp.data.total_documents ?? 0);
        } else {
          setCount(0);
        }
      } catch {
        if (!cancelled) setCount(0);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded) {
    return (
      <Pill $tone="muted">
        <PillIcon $tone="muted">
          <Database size={16} />
        </PillIcon>
        <PillBody>
          <PillTitle>Checking your Tender Bank…</PillTitle>
        </PillBody>
      </Pill>
    );
  }

  if (count && count > 0) {
    return (
      <Pill $tone="primary">
        <PillIcon $tone="primary">
          <Database size={16} />
        </PillIcon>
        <PillBody>
          <PillTitle>
            Drawing on {count} tender{count === 1 ? '' : 's'} from your Tender Bank
          </PillTitle>
          <PillDescription>
            The AI will use winning patterns from these documents during retrieval — no selection needed.
          </PillDescription>
        </PillBody>
        <PillAction to="/tender-bank">Manage</PillAction>
      </Pill>
    );
  }

  return (
    <Pill $tone="muted">
      <PillIcon $tone="muted">
        <Database size={16} />
      </PillIcon>
      <PillBody>
        <PillTitle>Tender Bank is empty</PillTitle>
        <PillDescription>
          Analysis will rely solely on your Knowledge Base. Add winning tenders to improve future drafts.
        </PillDescription>
      </PillBody>
      <PillAction to="/tender-bank">
        <Plus size={14} /> Add tenders
      </PillAction>
    </Pill>
  );
};

export default SuccessfulTenderUploader;
